"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import { useUserWebSocket } from "@/app/components/hooks/useUserWebSocket";
import useInitializeUserFromCookie from "@/app/components/hooks/useInitializeUserFromCookie";
import {
  fetchNotificationsApi,
  getNotifications,
  getUnreadCount,
  isUnreadNotification,
  markAllAsRead as apiMarkAllAsRead,
  markAsRead as apiMarkAsRead,
  discardNotification as apiDiscardNotification,
  PersistedNotificationItem,
} from "@/app/services/notifications.service";
import {
  isPersistedNotificationType,
  isPersistedSocketNotification,
  NotificationItem,
  normalizeNotificationItem,
  socketPayloadToNotificationItem,
} from "@/types/notification.types";
import {
  NotificationPayload,
  NotificationType,
} from "@/app/lib/models/NotificationModel";
import { AppConstants } from "@/app/lib/util/AppConstants";
import { HttpStatus } from "@/app/lib/models/ResponseFormatModel";
import { canDiscardNotification } from "@/app/lib/util/notification-priority.util";
import logger from "@/lib/logger";

type NotificationListener = (notification: NotificationPayload) => void;

interface NotificationContextValue {
  notifications: NotificationItem[];
  unreadCount: number;
  hasMore: boolean;
  isConnected: boolean;
  emailId: string;
  markRead: (id: string) => Promise<void>;
  discard: (id: string) => Promise<void>;
  clearAll: () => Promise<void>;
  markAllRead: () => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: (options?: { syncReminders?: boolean }) => Promise<void>;
  subscribeToNotifications: (listener: NotificationListener) => () => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(
  null,
);

function isAllowedForRole(role: string, type: string): boolean {
  if (role === AppConstants.Roles.TRAINEE) {
    return (
      type === NotificationType.MEMBERS_ADDED ||
      type === "members_added" ||
      type === NotificationType.ACCOUNT_UPGRADE ||
      type === "account_upgrade" ||
      type === NotificationType.SUBSCRIPTION_UPDATE ||
      type === "subscription_update" ||
      type === NotificationType.PAYMENT_SUCCESS ||
      type === "payment_success"
    );
  }
  return (
    role === AppConstants.Roles.GUARDIAN ||
    role === AppConstants.Roles.CENTER_HEAD ||
    role === AppConstants.Roles.ADMIN
  );
}

function toDrawerNotifications(
  items: PersistedNotificationItem[],
  role: string,
): NotificationItem[] {
  return items
    .filter((item) => isUnreadNotification(item))
    .filter((item) => isAllowedForRole(role, item.type))
    .map((item) => normalizeNotificationItem(item));
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const currentUrl = usePathname() as string;
  const userDetailsFromCookie = useInitializeUserFromCookie(currentUrl);
  const emailId = userDetailsFromCookie?.email_id ?? "";
  const sessionId = userDetailsFromCookie?.login_session_id;
  const userId = (userDetailsFromCookie as { user_id?: string } | undefined)
    ?.user_id;
  const role = userDetailsFromCookie?.role ?? "";

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const listenersRef = useRef<Set<NotificationListener>>(new Set());

  const refresh = useCallback(
    async (options?: { syncReminders?: boolean }) => {
      if (!emailId || !role) {
        return;
      }

      try {
        const [listResponse, count] = await Promise.all([
          fetchNotificationsApi(
            sessionId,
            emailId,
            30,
            options?.syncReminders ?? false,
          ),
          getUnreadCount(emailId, sessionId),
        ]);

        if (
          listResponse.status === HttpStatus.OK &&
          Array.isArray(listResponse.data)
        ) {
          const items = listResponse.data as PersistedNotificationItem[];
          const drawerItems = toDrawerNotifications(items, role);
          const allowedUnreadCount = items.filter(
            (item) =>
              isUnreadNotification(item) && isAllowedForRole(role, item.type),
          ).length;
          setNotifications(drawerItems);
          setHasMore(items.length === 30);
          setUnreadCount(allowedUnreadCount);
        } else {
          logger.warn("NotificationProvider: failed to load notifications", {
            status: listResponse.status,
            dataType: typeof listResponse.data,
          });
          setNotifications([]);
          setHasMore(false);
          setUnreadCount(count);
        }
      } catch (error) {
        logger.error("NotificationProvider: refresh failed", {
          error: String(error),
        });
      }
    },
    [emailId, sessionId, role],
  );

  useEffect(() => {
    if (!emailId || !role) return;
    void refresh({ syncReminders: true });
  }, [emailId, role, sessionId, refresh]);

  useEffect(() => {
    if (!emailId) return;

    const handleNotificationEvent = () => {
      void refresh();
    };

    window.addEventListener("notification-received", handleNotificationEvent);
    window.addEventListener("data-refresh-requested", handleNotificationEvent);

    return () => {
      window.removeEventListener(
        "notification-received",
        handleNotificationEvent,
      );
      window.removeEventListener(
        "data-refresh-requested",
        handleNotificationEvent,
      );
    };
  }, [emailId, refresh]);

  const notifyListeners = useCallback((notification: NotificationPayload) => {
    listenersRef.current.forEach((listener) => listener(notification));
  }, []);

  const handleSocketNotification = useCallback(
    (notification: NotificationPayload) => {
      const data = (
        notification as NotificationPayload & { data?: Record<string, unknown> }
      ).data;
      const shouldUpdateBell =
        isPersistedSocketNotification(data) ||
        isPersistedNotificationType(notification.type);

      if (shouldUpdateBell) {
        const newItem = socketPayloadToNotificationItem({
          type: notification.type,
          title: notification.title,
          message: notification.message,
          data,
        });
        if (isAllowedForRole(role, newItem.type)) {
          setNotifications((prev) => {
            if (prev.some((item) => item.id === newItem.id)) return prev;
            return [newItem, ...prev];
          });
          setUnreadCount((count) => count + 1);
        }
      }

      notifyListeners(notification);
    },
    [notifyListeners, role],
  );

  const { isConnected } = useUserWebSocket({
    userId,
    emailId,
    onNotification: handleSocketNotification,
    enabled: !!emailId,
    currentPage: currentUrl,
  });

  const markRead = useCallback(
    async (id: string) => {
      setNotifications((prev) => prev.filter((item) => item.id !== id));
      setUnreadCount((count) => Math.max(0, count - 1));
      await apiMarkAsRead(emailId, sessionId, id);
    },
    [emailId, sessionId],
  );

  const discard = useCallback(
    async (id: string) => {
      setNotifications((prev) => prev.filter((item) => item.id !== id));
      setUnreadCount((count) => Math.max(0, count - 1));
      await apiDiscardNotification(emailId, sessionId, id);
    },
    [emailId, sessionId],
  );

  /** Dismiss all except high-priority and incomplete task reminders. */
  const clearAll = useCallback(async () => {
    if (!emailId) return;

    const dismissible = notifications.filter((item) =>
      canDiscardNotification(item),
    );
    if (dismissible.length === 0) return;

    const dismissibleIds = new Set(dismissible.map((item) => item.id));
    setNotifications((prev) =>
      prev.filter((item) => !dismissibleIds.has(item.id)),
    );
    setUnreadCount((count) => Math.max(0, count - dismissible.length));

    await Promise.all(
      dismissible.map((item) =>
        apiDiscardNotification(emailId, sessionId, item.id).catch((error) => {
          logger.warn("Failed to discard notification during clear all", {
            id: item.id,
            error: String(error),
          });
        }),
      ),
    );

    await refresh({ syncReminders: false });
  }, [emailId, sessionId, notifications, refresh]);

  const markAllRead = useCallback(async () => {
    await apiMarkAllAsRead(emailId, sessionId);
    await refresh({ syncReminders: false });
  }, [emailId, sessionId, refresh]);

  const loadMore = useCallback(async () => {
    if (!hasMore || notifications.length === 0 || !emailId || !role) return;

    const oldest = notifications[notifications.length - 1];
    const before = oldest.created_at._seconds * 1000;
    const more = await getNotifications(emailId, sessionId, 20, before);
    const unreadForRole = more.filter(
      (item) => !item.is_read && isAllowedForRole(role, item.type),
    );
    setNotifications((prev) => [...prev, ...unreadForRole]);
    setHasMore(more.length === 20);
  }, [emailId, sessionId, hasMore, notifications, role]);

  const subscribeToNotifications = useCallback(
    (listener: NotificationListener) => {
      listenersRef.current.add(listener);
      return () => {
        listenersRef.current.delete(listener);
      };
    },
    [],
  );

  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications,
      unreadCount,
      hasMore,
      isConnected,
      emailId,
      markRead,
      discard,
      clearAll,
      markAllRead,
      loadMore,
      refresh,
      subscribeToNotifications,
    }),
    [
      notifications,
      unreadCount,
      hasMore,
      isConnected,
      emailId,
      markRead,
      discard,
      clearAll,
      markAllRead,
      loadMore,
      refresh,
      subscribeToNotifications,
    ],
  );

  useEffect(() => {
    if (emailId && isConnected) {
      logger.info(
        "NotificationProvider: WebSocket connected for user notifications",
      );
    }
  }, [emailId, isConnected]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotificationContext must be used within NotificationProvider",
    );
  }
  return context;
}

export default NotificationProvider;
